import * as React from 'react';
import moment from 'moment';
import {
    Icon, Popup,
} from 'semantic-ui-react';

import { tl, hasTL } from '../../services/i18nService';
import { WorkspaceGroup, WorkspaceItem } from '../../store/common/workspaceScheme/types';
import commonWorkspaceSchemeService from '../../services/commonWorkspaceSchemeService';

import * as styles from './Sidebar.scss';
import { CommonContextState } from '../../store/context/types';
import { newWorkspaceLevels, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS, workspaceLevels } from '../../store/common/constants';
import { isQuestionMode, isSection } from '../../util/shared/sharedUtils';

interface CollapsibleSubMenuProps {
    group: WorkspaceGroup;
    context: CommonContextState;
    navigationCallback: (workspaceId: string) => void;
}

interface CollapsibleSubMenuState {
    expanded: boolean;
}

interface WorkspaceLevel {
    id: string;
    level: number;
}

class CollapsibleSubMenu extends React.Component<CollapsibleSubMenuProps, CollapsibleSubMenuState> {
    constructor(props: CollapsibleSubMenuProps) {
        super(props);

        this.state = {
            expanded: true,
        };
    }

    toggleSidebar = (): void => {
        const { expanded } = this.state;
        this.setState({
            expanded: !expanded,
        });
    };

    renderVisibilityCaptions(workspaceRef: WorkspaceItem): React.ReactElement {
        if (!workspaceRef.visible && workspaceRef.visibleFrom) {
            const date = moment.utc(workspaceRef.visibleFrom, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS).local().format('YYYY-MM-DD HH:mm');
            return (
                <p>{`${tl('grading.sidebars.workspaceScheme.visibleFrom')} ${date}`}</p>
            );
        }
        if (!workspaceRef.visible && workspaceRef.visibleWhen) {
            return (
                <p>
                    {`${tl('grading.sidebars.workspaceScheme.visibleWhen')} \
                    ${tl(`grading.features.workspaceSchemes.conditions.${workspaceRef.visibleWhen}`)}`}
                </p>
            );
        }

        return null;
    }

    render(): React.ReactElement {
        const { expanded } = this.state;
        const {
            context,
            group,
        } = this.props;
        const groupTitle = tl(`workspaceScheme.${group.type}.title`);
        const groupDescKey = `workspaceScheme.${group.type}.description`;
        const groupDescription = hasTL(groupDescKey) ? tl(`workspaceScheme.${group.type}.description`) : null;
        const groupTitleId = `title_${groupTitle}`;
        const linkGroupId = `list_${groupTitle}`;
        const isTemplate = context?.currentItemContext?.isTemplate;
        const isXTMEnabled = window.marketplaceProperties.ENABLE_ITEM_REVIEW_XTM;
        const isItemReviewEnabled = window.marketplaceProperties.ENABLE_ITEM_REVIEW;
        return (
            <div className={styles.subMenuWrapper}>
                <button
                    className={`${styles.subMenu} ${expanded ? 'expanded' : ''} custom-focus-border sub-menu`}
                    onClick={this.toggleSidebar}
                    type="button"
                    aria-controls={expanded ? linkGroupId : null}
                    aria-expanded={expanded}
                    aria-details={groupDescription}
                >
                    <h3 id={groupTitleId}>
                        <span className="sr-only">{tl('common.sidebars.submenu')}</span>
                        {groupTitle}
                    </h3>
                    {
                        groupDescription && (
                            <Popup
                                content={groupDescription}
                                trigger={(
                                    <Icon
                                        tabIndex={0}
                                        name="question circle"
                                        size="small"
                                    />
                                )}
                                on={['focus', 'hover']}
                                position="bottom right"
                                hideOnScroll
                            />
                        )
                    }
                    <span className={`${styles.minimizeSubMenu}`}>
                        { expanded
                            ? <Icon name="angle up" size="large" />
                            : <Icon name="angle down" size="large" />}
                    </span>
                </button>
                { expanded && (
                    <ul
                        className={`${styles.subMenuList} submenu-list`}
                        id={linkGroupId}
                        aria-labelledby={groupTitleId}
                    >
                        {
                            group.workspaces.map((workspaceRef) => {
                                const workspaceId = commonWorkspaceSchemeService.getWorkspaceIdentifier(workspaceRef);
                                const active = workspaceId === context.workspaceId;
                                const { ENABLE_FILTER_INCOMPLETE_MARKED_QUESTIONS } = window.marketplaceProperties;
                                const isIncompleteMarkedQuestionFilterEnabled = ENABLE_FILTER_INCOMPLETE_MARKED_QUESTIONS;
                                const getIndentationLevel = (workspaceId: string): number => {
                                    const workspaceLevelsList = isIncompleteMarkedQuestionFilterEnabled
                                        ? newWorkspaceLevels
                                        : workspaceLevels;
                                    const workspace = workspaceLevelsList.find((w) => w.id === workspaceId);
                                    return workspace ? Math.min(workspace.level, 3) : -1;
                                };
                                const indentationLevel = getIndentationLevel(workspaceId);
                                let linkStyle = active ? 'active' : '';
                                if (!active && !workspaceRef.visible) {
                                    linkStyle = styles.itemDisabled;
                                }

                                const loadPreset = (): void => (
                                    !active && this.props.navigationCallback(workspaceId)
                                );
                                const isCurrentXTM = commonWorkspaceSchemeService.getWorkspaceIdentifier(workspaceRef) === 'item_review_xtm';
                                const shouldRename = (isQuestionMode() || isSection())
                                    && !isTemplate && !isItemReviewEnabled && isXTMEnabled && isCurrentXTM;
                                const workspaceIdentifier = commonWorkspaceSchemeService.getWorkspaceIdentifier(workspaceRef);
                                const isPlanAssessmentRunQuestions = workspaceIdentifier === 'plan_assessmentrun_questions';
                                const isEvaluateAssessmentRunQuestions = workspaceIdentifier === 'evaluate_assessmentrun_questions';

                                let title;
                                if (shouldRename) {
                                    title = tl('workspace.item_bank_default.title');
                                } else if (
                                    isIncompleteMarkedQuestionFilterEnabled
                                    && (isPlanAssessmentRunQuestions || isEvaluateAssessmentRunQuestions)
                                ) {
                                    const workspaceTitle = tl(`workspace.${workspaceIdentifier}.title`);
                                    const overviewTitle = tl('workspace.Overview');
                                    title = `${workspaceTitle} ${overviewTitle}`;
                                } else {
                                    title = tl(`workspace.${workspaceIdentifier}.title`);
                                }
                                if (workspaceRef.visible || workspaceRef.visibleFrom || workspaceRef.visibleWhen) {
                                    const PADDING_MULTIPLIER = 15;
                                    const indentedSubmenu = indentationLevel > 0 ? { paddingLeft: `${indentationLevel * PADDING_MULTIPLIER}px` } : {};
                                    return (
                                        <li
                                            key={workspaceId}
                                        >
                                            <button
                                                type="button"
                                                title={title}
                                                onClick={loadPreset}
                                                id={workspaceId}
                                                className={`${linkStyle} custom-focus-border`}
                                                style={indentedSubmenu}
                                            >
                                                {indentationLevel > 0 && (
                                                    <span className={styles.indentMarker}> | </span>
                                                )}
                                                <span>
                                                    <p className={styles.itemTitle}>{title}</p>
                                                    {this.renderVisibilityCaptions(workspaceRef)}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                }
                                return undefined;
                            })
                        }
                    </ul>
                )}
            </div>
        );
    }
}

export default CollapsibleSubMenu;
